import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import BookEvaluation from './pages/BookEvaluation';
import MiniIOPEnrollment from './pages/MiniIOPEnrollment';
import TrainingPortal from './pages/TrainingPortal';
import DigitalShop from './pages/DigitalShop';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBookings from './pages/admin/AdminBookings';
import AdminCourses from './pages/admin/AdminCourses';
import AdminProducts from './pages/admin/AdminProducts';
import AdminContacts from './pages/admin/AdminContacts';
import AdminAppLeads from './pages/admin/AdminAppLeads';
import AdminSubscribers from './pages/admin/AdminSubscribers';
import AdminBlog from './pages/admin/AdminBlog';
import Blog from './pages/Blog';
import SupportChat from './components/SupportChat';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-blue)]"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0c1929]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/40"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="courses" element={<AdminCourses />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="app-leads" element={<AdminAppLeads />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="blog" element={<AdminBlog />} />
        </Route>
      </Routes>
    );
  }

  return (
    <PublicLayout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/book-evaluation" element={<ProtectedRoute><BookEvaluation /></ProtectedRoute>} />
        <Route path="/mini-iop" element={<ProtectedRoute><MiniIOPEnrollment /></ProtectedRoute>} />
        <Route path="/training" element={<TrainingPortal />} />
        <Route path="/shop" element={<DigitalShop />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/payment-cancel" element={<PaymentCancel />} />
      </Routes>
    </PublicLayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <SupportChat />
        <Toaster position="top-right" richColors />
      </Router>
    </AuthProvider>
  );
}

export default App;
